import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { SlotCreationStatus } from 'src/modals/EventGroup/EventGroup.types';
import * as creatingStyles from 'src/modals/EventGroup/steps/EventGroupCreatingStep.css';
import { CheckIcon, ErrorIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { SequentialBatchRunner } from 'src/utils/sequentialBatchRunner';
import {
  AllocatePlaceholderEventRequest,
  CatalogResults,
  EventGroupClient,
  Performer,
  Venue,
} from 'src/WebApiController';

import { EventSearchResult } from '../EventSearch';
import { EventSearchItem } from '../EventSearch/EventSearchItem';
import { matchUpPlaceholderEventsAndCatalogEvents } from './utils';

interface AllocationSubmissionStepProps {
  selectedEvents: EventSearchResult[];
  posEventIdsToAllocate: string[];
  onComplete: () => void; // called when ALL requests have resolved (success or error)
}

// ─── StatusIcon ───────────────────────────────────────────────────────────────

const StatusIcon = ({ status }: { status: SlotCreationStatus }) => {
  switch (status) {
    case 'pending':
    case 'in-progress':
      return <PosSpinner size={vars.iconSize.xxs} centered={false} />;
    case 'done':
      return (
        <CheckIcon size={vars.iconSize.xl} stroke={vars.color.iconSuccess} />
      );
    case 'error':
      return <ErrorIcon size={vars.iconSize.l} />;
  }
};

const AllocationSubmissionStepRaw = ({
  selectedEvents,
  posEventIdsToAllocate,
  onComplete,
}: AllocationSubmissionStepProps) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  // Runs the allocation POSTs sequentially (one after another) rather than
  // firing them all at once, and reports each request's status back per-row.
  const allocator = useMemo(() => {
    const client = new EventGroupClient(activeAccountWebClientConfig);
    return new SequentialBatchRunner<AllocatePlaceholderEventRequest>(
      (request) => client.allocatePlaceholderEvent(request),
      (error) =>
        showErrorDialog(
          'EventGroupClient.allocatePlaceholderEvent',
          error as Error
        )
    );
  }, [activeAccountWebClientConfig, showErrorDialog]);

  // The allocation requests, in the same order they're submitted. May be fewer
  // than selectedEvents — pairs missing viagId/startDate are skipped by the
  // matcher — so every displayed row maps to exactly one live request/status.
  const requests = useMemo(
    () =>
      matchUpPlaceholderEventsAndCatalogEvents(
        selectedEvents,
        posEventIdsToAllocate
      ),
    [posEventIdsToAllocate, selectedEvents]
  );

  // Each request paired back to the catalog event it allocates (matched on the
  // viagogo id), so we can render the corresponding EventSearchItem per row.
  const eventForRequest = useMemo(
    () =>
      requests.map((req) =>
        selectedEvents.find((e) => e.event.viagId === req.viagogoEventId)
      ),
    [requests, selectedEvents]
  );

  // Minimal catalog for EventSearchItem's performer/venue lookup. We already
  // have both on each selected event, so we just re-key them by perf/venue id.
  const catalog = useMemo(() => {
    const performers: Record<string, Performer> = {};
    const venues: Record<string, Venue> = {};
    for (const { event, performer, venue } of selectedEvents) {
      if (performer && event.perfId != null) {
        performers[event.perfId] = performer;
      }
      if (venue) {
        venues[event.venId] = venue;
      }
    }
    return { performers, venues } as CatalogResults;
  }, [selectedEvents]);

  const [allocationStatuses, setAllocationStatuses] = useState<
    SlotCreationStatus[]
  >(Array.from({ length: requests.length }, () => 'pending'));

  const updateAllocationStatus = (
    index: number,
    newStatus: SlotCreationStatus
  ) => {
    setAllocationStatuses((current) =>
      current.map((s, i) => (i === index ? newStatus : s))
    );
  };

  const onAllocationDone = useCallback(
    (succeedCount: number, failedCount: number) => {
      onComplete();
    },
    [onComplete]
  );

  // TODO: Move submission logic to an explicit event handler triggered by
  // the parent flow (e.g. a "Submit" button click) to avoid re-firing on
  // unexpected re-mounts. The current useEffect-on-mount pattern is fragile
  // because any parent re-render that unmounts/remounts this component will
  // re-trigger the cost-update and allocation POST requests.
  // See PR #5812 review comment: https://github.com/stubhub/StubHub.PointOfSale/pull/5812#discussion_r3561104786

  // Run submission steps on mount
  useEffect(() => {
    const run = async () => {
      // Step 2: Run allocations one after another.
      // `requests` (memoized above) may contain fewer entries than selectedEvents
      // — the matcher skips pairs missing viagId or startDate. allocationStatuses
      // is sized to exactly requests.length so every displayed row has a
      // corresponding live status — no phantom "pending" rows. The allocator
      // flips each row to 'in-progress' as it starts and 'done'/'error' as it
      // settles, so the queue's progress is visible row by row.
      const unsubscribe = allocator.subscribe(updateAllocationStatus);
      const { succeeded, failed } = await allocator.run(requests);
      unsubscribe();
      setTimeout(() => onAllocationDone(succeeded, failed), 100);
    };

    run();

    // Stop the queue if the step unmounts mid-flight.
    return () => {
      allocator.cancel();
    };
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack direction="column" gap="m" className={creatingStyles.container}>
      <div>
        <div className={creatingStyles.title}>
          <Content id={ContentId.AllocationSubmittingTitle} />
        </div>
        <div className={creatingStyles.subtitle}>
          <Content id={ContentId.AllocationSubmittingSubtitle} />
        </div>
      </div>

      <Stack direction="column" gap="m">
        {requests.map((req, i) => {
          const result = eventForRequest[i];
          if (!result) {
            return null;
          }
          return (
            <Stack
              key={req.viagogoEventId ?? i}
              direction="row"
              gap="s"
              alignItems="center"
            >
              <div className={creatingStyles.statusIcon}>
                <StatusIcon status={allocationStatuses[i] ?? 'pending'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <EventSearchItem
                  event={result.event}
                  catalog={catalog}
                  onChecked={() => {}}
                  displayOnly
                />
              </div>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

export const AllocationSubmissionStep = memo(AllocationSubmissionStepRaw);
